/** Minimal DOM helpers — enough structure to avoid innerHTML string soup. */

type Attrs = Record<string, string | number | boolean | null | undefined>;
type Child = Node | string | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  for (const [name, value] of Object.entries(attrs)) {
    if (value === null || value === undefined || value === false) continue;
    if (name === "class") node.className = String(value);
    else if (name === "text") node.textContent = String(value);
    else if (name === "html") node.innerHTML = String(value);
    else if (name.startsWith("data-") || name.startsWith("aria-")) {
      node.setAttribute(name, String(value));
    } else if (value === true) node.setAttribute(name, "");
    else node.setAttribute(name, String(value));
  }

  append(node, ...children);
  return node;
}

export function append(parent: Node, ...children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.appendChild(typeof child === "string" ? document.createTextNode(child) : child);
  }
}

export function clear(node: Node): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function on<K extends keyof HTMLElementEventMap>(
  node: HTMLElement,
  type: K,
  handler: (event: HTMLElementEventMap[K]) => void,
): void {
  node.addEventListener(type, handler as EventListener);
}

export function mustFind<T extends HTMLElement = HTMLElement>(selector: string): T {
  const node = document.querySelector<T>(selector);
  if (!node) throw new Error(`Missing required element: ${selector}`);
  return node;
}

/**
 * Bootstrap card scaffold. `actions` sits on the right of the header, which is
 * where every panel puts its primary control.
 */
export function card(
  title: string,
  body: HTMLElement,
  actions?: HTMLElement | null,
): HTMLElement {
  return el(
    "section",
    { class: "card shadow-sm" },
    el(
      "div",
      { class: "card-header d-flex align-items-center justify-content-between gap-2" },
      el("h2", { class: "card-title h6 mb-0 text-uppercase", text: title }),
      actions ?? null,
    ),
    el("div", { class: "card-body" }, body),
  );
}

/**
 * A label with a hover/focus explanation. The original site leans heavily on
 * these and they carry real information — what a toggle costs, why a number
 * matters — so they are worth keeping.
 */
export function labelWithHelp(text: string, help: string): HTMLElement {
  return el(
    "span",
    { class: "d-inline-flex align-items-center gap-1" },
    el("span", { text }),
    el("button", {
      type: "button",
      class: "btn-help",
      "data-bs-toggle": "tooltip",
      "data-bs-placement": "top",
      "aria-label": help,
      title: help,
      text: "?",
    }),
  );
}

export function badge(text: string, variant = "secondary"): HTMLElement {
  return el("span", { class: `badge text-bg-${variant}`, text });
}

export function icon(name: "search" | "spinner" | "external"): HTMLElement {
  const spans: Record<string, string> = {
    search: "⌕",
    spinner: "◌",
    external: "↗",
  };
  return el("span", { class: `glyph glyph-${name}`, "aria-hidden": "true", text: spans[name] ?? "" });
}
